import { differenceInDays, differenceInHours } from 'date-fns';

import type APIAddress from '~/types/APIAddress';
import type Address from '~/types/Address';
import type Order from '~/types/Order';

import { type OrderV2ShipmentAddress } from '~/modules/orders/types/ordersV2.type';
import { type APIError } from '~/types/APIError';
import { isAddressIncomplete } from '~/types/Address';
import { getCustomsPhoneErrors } from '~/types/CustomsDeclaration';
import { type OrderSelection } from '~/types/Order';
import { isFulfilled, requiresCustomsDeclaration as isOrderRequiresCustoms } from '~/types/Order';
import { requiresCustomsDeclaration, type BatchEntryExtended } from '~/types/OrderBatchEntry';

export type SelectedOrderErrorKeys =
  | 'addressesErrors'
  | 'carrierParcelNoRatesErrors'
  | 'customsErrors'
  | 'dateErrors'
  | 'packageErrors'
  | 'recipientErrors'
  | 'returnErrors'
  | 'senderErrors';

type SelectedOrdersErrorsCountKey = Exclude<SelectedOrderErrorKeys, 'addressesErrors'>;

export type SelectedOrdersErrorsCount = { [k in SelectedOrdersErrorsCountKey]: number };

export type RatingErrors = { [k in SelectedOrdersErrorsCountKey]: boolean };

export const getOrderErrors = (
  selectedOrders: OrderSelection,
  batchEntries: BatchEntryExtended[],
  ordersData: {
    [orderId: string]: Order;
  },
  defaultUserAddress?: APIAddress,
  defaultReturnAddress?: APIAddress,
  ratesByShipmentObjectID?: HashMap<string, readonly string[]>,
): SelectedOrdersErrorsCount => {
  const shipments: { batchEntry?: BatchEntryExtended; order?: Order }[] = [
    ...Object.entries(selectedOrders)
      .filter(([, batchEntryIds]) => batchEntryIds.length === 0)
      .map(([orderId]) => ({ order: ordersData[orderId] })),
    ...batchEntries.map((batchEntry) => ({
      batchEntry,
      order: ordersData[batchEntry.orderObjectId],
    })),
  ];

  const ratingErrors = shipments
    .filter((shipment): shipment is { batchEntry?: BatchEntryExtended; order: Order } =>
      Boolean(shipment.order),
    )
    .map(({ batchEntry, order }) =>
      getRatingErrors({
        batchEntry,
        defaultReturnAddress,
        defaultUserAddress,
        order,
        ratesByShipmentObjectID,
      }),
    );

  return aggregateRatingErrors(ratingErrors);
};

export type SelectedOrdersErrors = ReturnType<typeof getOrderErrors>;

export const orderHasCarrierParcelNoRatesError = (
  batchEntry: BatchEntryExtended,
  ratesByShipmentObjectID?: HashMap<string, readonly string[]>,
) =>
  batchEntry.parcel?.carrierParcelTemplate &&
  batchEntry.shipmentObjectId &&
  ratesByShipmentObjectID &&
  batchEntry.shipmentObjectId in ratesByShipmentObjectID &&
  ratesByShipmentObjectID[batchEntry.shipmentObjectId].length === 0;

export const orderHasCustomsError = (
  order: Order,
  batchEntry?: BatchEntryExtended,
  defaultUserAddress?: APIAddress,
) => {
  const isBypassCustoms = !!batchEntry?.extras?.isBypassCustoms;
  const requiresCustoms = batchEntry
    ? requiresCustomsDeclaration(batchEntry) && !isBypassCustoms
    : isOrderRequiresCustoms(order, defaultUserAddress);
  const customsItems = batchEntry?.customsItems?.length ? batchEntry.customsItems : [];
  return requiresCustoms && !isFulfilled(order) && !customsItems.length;
};

export const orderHasShipmentDateError = (batchEntry?: BatchEntryExtended) => {
  const now = new Date();
  const shipmentDate = batchEntry?.shipmentDate ? batchEntry.shipmentDate.toDate() : now;
  const isWithinPast24Hours = differenceInHours(now, shipmentDate) <= 24;
  const isWithinNext5Days = differenceInDays(shipmentDate, now) <= 5;
  return !isWithinPast24Hours || !isWithinNext5Days;
};

export const orderHasPackageError = (order: Order | undefined, batchEntry?: BatchEntryExtended) => {
  const hasParcelError = !batchEntry?.parcel;
  const hasParcelWeightErrors = batchEntry
    ? !(Number(batchEntry.parcel?.weight) > 0 && batchEntry.parcel?.weightUnit)
    : !(Number(order?.parcel?.weight) > 0 && order?.parcel?.weightUnit);
  const batchEntryErrors = batchEntry?.errors ? [...batchEntry.errors] : [];
  const orderErrors =
    order &&
    (batchEntry?.partialFulfillmentRank === 0 || order.partialFulfillmentRequests?.length === 0)
      ? [...(order.errors || [])]
      : [];
  const errors: APIError[] = batchEntryErrors.length ? batchEntryErrors : orderErrors;
  const hasExceededPackageWeightError = !!errors.find(
    (error) => error.code === 'order_weight_exceeded',
  );

  return hasParcelError || hasParcelWeightErrors || hasExceededPackageWeightError;
};

export const orderHasRecipientError = ({
  addressFrom,
  addressTo,
}: {
  addressFrom?: Address | OrderV2ShipmentAddress | null;
  addressTo?: Address | OrderV2ShipmentAddress | null;
}) => {
  const { hasPhoneNumberRequiredError } = getCustomsPhoneErrors({ addressFrom, addressTo });
  if (hasPhoneNumberRequiredError) return true;

  if (isAddressIncomplete(addressTo)) return true;
  return false;
};

export const getRatingErrors = ({
  batchEntry,
  defaultReturnAddress,
  defaultUserAddress,
  order,
  ratesByShipmentObjectID,
}: {
  batchEntry?: BatchEntryExtended;
  defaultReturnAddress?: APIAddress;
  defaultUserAddress?: APIAddress;
  order: Order;
  ratesByShipmentObjectID?: HashMap<string, readonly string[]>;
}): RatingErrors | null => {
  const senderAddress = batchEntry?.addressFrom ?? order.addressFrom ?? defaultUserAddress;
  const returnAddress = batchEntry?.addressReturn ?? order.addressReturn ?? defaultReturnAddress;

  const ratingErrors: RatingErrors = {
    carrierParcelNoRatesErrors: Boolean(
      batchEntry && orderHasCarrierParcelNoRatesError(batchEntry, ratesByShipmentObjectID),
    ),
    customsErrors: orderHasCustomsError(order, batchEntry, defaultUserAddress),
    dateErrors: orderHasShipmentDateError(batchEntry),
    packageErrors: orderHasPackageError(order, batchEntry),
    recipientErrors: orderHasRecipientError({
      addressFrom: batchEntry ? batchEntry.addressFrom : order.addressFrom,
      addressTo: batchEntry ? batchEntry.addressTo : order.toAddress,
    }),
    returnErrors: isAddressIncomplete(returnAddress),
    senderErrors: isAddressIncomplete(senderAddress),
  };

  return Object.values(ratingErrors).some(Boolean) ? ratingErrors : null;
};

export const aggregateRatingErrors = (
  ratingErrorsList: (RatingErrors | null | undefined)[],
): SelectedOrdersErrorsCount => {
  const counts: SelectedOrdersErrorsCount = {
    carrierParcelNoRatesErrors: 0,
    customsErrors: 0,
    dateErrors: 0,
    packageErrors: 0,
    recipientErrors: 0,
    returnErrors: 0,
    senderErrors: 0,
  };

  for (const ratingErrors of ratingErrorsList) {
    if (!ratingErrors) continue;
    for (const key of Object.keys(counts) as (keyof SelectedOrdersErrorsCount)[])
      if (ratingErrors[key]) counts[key] += 1;
  }

  return counts;
};

export const getIncompleteOrderErrorKeys = (
  errors: SelectedOrdersErrorsCount,
): SelectedOrderErrorKeys[] => {
  if (!errors) return [];

  const errorList: SelectedOrderErrorKeys[] = [];
  for (const [key, value] of Object.entries(errors))
    if (value >= 1) errorList.push(key as keyof SelectedOrdersErrorsCount);

  return errorList;
};
